import { useQuery } from '@tanstack/react-query'

import {
  createParser,
  parseAsNativeArrayOf,
  parseAsNumberLiteral,
  parseAsString,
  useQueryStates,
} from 'nuqs'

import { isValidISOWeek } from '@util/date-util'

import { useAuthenticatedUser } from '../../../auth/hooks/useAuthenticatedUser'
import {
  DEFAULT_PLANNING_LIMIT,
  PLANNING_LIMIT_OPTIONS,
  initialState,
  searchProjecten,
} from '../services/searchProjectenPlanning'
import type { ProjectenPlanningSearchFilters } from '../types/ProjectenPlanningSearch'

export { useProjectenPlanningSearch, queryKey }

const queryKey = 'hoofdleiding-projecten-planning'

const { fromWeek, toWeek } = initialState.query.filters

const parseAsStrings = parseAsNativeArrayOf(parseAsString)

// An empty week is valid: it leaves out the week filter
const parseAsISOWeek = createParser({
  parse: (value) => (value === '' || isValidISOWeek(value) ? value : null),
  serialize: (value: string) => value,
})

const filtersParsers = {
  combi: parseAsString,
  processtap: parseAsStrings,
  gebied: parseAsStrings,
  labels: parseAsStrings,
  fromWeek: parseAsISOWeek.withDefault(fromWeek),
  toWeek: parseAsISOWeek.withDefault(toWeek),
  aannemer: parseAsString,
  typeWerk: parseAsStrings,
  gemeente: parseAsStrings,
  verantwoordelijke: parseAsString,
}

const pageParsers = {
  limit: parseAsNumberLiteral(PLANNING_LIMIT_OPTIONS).withDefault(
    DEFAULT_PLANNING_LIMIT,
  ),
}

const useProjectenPlanningSearch = () => {
  const [filters, setFilters] = useQueryStates(filtersParsers)
  const [{ limit }, setPage] = useQueryStates(pageParsers)
  const { hasPermissions } = useAuthenticatedUser()

  const query = { ...initialState.query, filters, page: { skip: 0, limit } }

  const { data, refetch, isFetching, isError } = useQuery({
    queryKey: [queryKey, query],
    queryFn: () => searchProjecten({ query }),
    enabled: hasPermissions,
    refetchOnMount: true,
  })

  return {
    data: data || initialState.data,
    isLoading: isFetching,
    isError,
    filters,
    onChangeFilters: (filters: Partial<ProjectenPlanningSearchFilters>) => {
      setFilters(filters)
    },
    limit,
    onChangeLimit: (limit: number) => {
      setPage({ limit })
    },
    onRefetch: refetch,
  }
}
